import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { sendEmailVerification } from 'firebase/auth';
import { auth } from '../firebase/firebase';
import { useAuth } from '../context/AuthContext';
import { notify } from '../utils/notifications';
import '../styles/VerifyEmail.css';

const RESEND_COOLDOWN_S = 60;

/**
 * C1: shown to a signed-in user whose Firebase email is not verified.
 * ProtectedRoute sends unverified users here; the backend rejects their
 * API calls with 403 email_unverified until the link in the email is opened.
 */
export const VerifyEmail = () => {
    const [cooldown, setCooldown] = useState(0);
    const [sending, setSending] = useState(false);
    const [checking, setChecking] = useState(false);
    const navigate = useNavigate();
    const { user, signOut } = useAuth();

    const email = auth.currentUser?.email ?? user?.email;

    useEffect(() => {
        if (cooldown <= 0) return undefined;
        const id = setInterval(() => setCooldown((s) => s - 1), 1000);
        return () => clearInterval(id);
    }, [cooldown]);

    const onResend = async () => {
        const current = auth.currentUser;
        if (!current) {
            notify.info('Still signing in. Try again in a moment.');
            return;
        }
        setSending(true);
        try {
            await sendEmailVerification(current);
            notify.success('Verification email sent');
            setCooldown(RESEND_COOLDOWN_S);
        } catch (err) {
            if (err?.code === 'auth/too-many-requests') {
                notify.error('Too many requests. Wait a few minutes before you resend.');
            } else {
                notify.error('Could not send the verification email. Try again in a minute.');
            }
        } finally {
            setSending(false);
        }
    };

    const onVerified = async () => {
        const current = auth.currentUser;
        if (!current) {
            notify.info('Still signing in. Try again in a moment.');
            return;
        }
        setChecking(true);
        try {
            await current.reload();
            // Next API call carries email_verified: true. This also refires
            // AuthContext's onIdTokenChanged listener, which updates emailVerified.
            await current.getIdToken(true);
            if (current.emailVerified) {
                navigate('/');
            } else {
                notify.warning('Not verified yet. Open the link in the email first.');
            }
        } catch (err) {
            notify.error('Could not check your verification status. Try again.');
        } finally {
            setChecking(false);
        }
    };

    const resendLabel = cooldown > 0 ? `Resend in ${cooldown} s` : 'Resend email';

    return (
        <div className="login-container verify-email-container">
            <div className="login-container-card verify-email-card">
                <h2>Verify your email</h2>
                <p className="verify-email-address">{email}</p>
                <p className="verify-email-copy">
                    We sent a verification link to this address. Open it, then come back and press I have verified.
                </p>
                <div className="verify-email-actions">
                    <button
                        type="button"
                        className="verify-email-secondary"
                        onClick={onResend}
                        disabled={cooldown > 0 || sending}
                    >
                        {resendLabel}
                    </button>
                    <button
                        type="button"
                        className="verify-email-primary"
                        onClick={onVerified}
                        disabled={checking}
                    >
                        I have verified
                    </button>
                </div>
                <button type="button" className="tos-link verify-email-signout" onClick={() => signOut()}>
                    Sign out
                </button>
            </div>
        </div>
    );
};
